import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Json } from "@/integrations/supabase/types";
import { testModeLogger } from "@/lib/logger";

export type TestPersonaType = 
  | "actor_kid" | "actor_tween" | "actor_teen" | "actor_adult"
  | "parent" | "coach" | "school" | "agent" | "manager"
  | "casting_professional" | "casting_associate" | "producer" | "studio" | "vendor";

export type TestPlatform = "callback-zone" | "coach" | "pro" | "castings";

export type SubscriptionTier = "none" | "free" | "basic" | "premium" | "enterprise" | "expired" | "trial";

export interface TestPersona {
  id: string;
  type: TestPersonaType;
  ageGroup?: "kid" | "tween" | "teen" | "adult";
  name: string;
  email: string;
  platform: TestPlatform;
  subscriptionTier: SubscriptionTier;
  roles: string[];
  description: string;
}

export interface TestAction {
  timestamp: string;
  action: "view" | "click" | "navigate" | "attempted_write" | "simulated_action";
  target: string;
  details: Record<string, unknown>;
}

interface TestModeContextType {
  isTestMode: boolean;
  testPersona: TestPersona | null;
  testSessionId: string | null;
  simulatedSubscription: SubscriptionTier;
  actionsLog: TestAction[];
  enterTestMode: (persona: TestPersona) => Promise<void>;
  exitTestMode: () => Promise<void>;
  setSimulatedSubscription: (tier: SubscriptionTier) => void;
  logTestAction: (action: Omit<TestAction, "timestamp">) => void;
  isWriteBlocked: boolean;
  canAccessTestMode: boolean;
}

const TestModeContext = createContext<TestModeContextType | undefined>(undefined);

// Pre-defined test personas for each platform and role
export const TEST_PERSONAS: TestPersona[] = [
  // Callback Zone - Actors
  {
    id: "test-actor-kid",
    type: "actor_kid",
    ageGroup: "kid",
    name: "Test Kid Actor",
    email: "test.kid@sandbox.castready.dev",
    platform: "callback-zone",
    subscriptionTier: "premium",
    roles: ["actor"],
    description: "Young actor (5-9) with parent oversight",
  },
  {
    id: "test-actor-tween",
    type: "actor_tween",
    ageGroup: "tween",
    name: "Test Tween Actor",
    email: "test.tween@sandbox.castready.dev",
    platform: "callback-zone",
    subscriptionTier: "premium",
    roles: ["actor"],
    description: "Pre-teen actor (10-12) with guided experience",
  },
  {
    id: "test-actor-teen",
    type: "actor_teen",
    ageGroup: "teen",
    name: "Test Teen Actor",
    email: "test.teen@sandbox.castready.dev",
    platform: "callback-zone",
    subscriptionTier: "premium",
    roles: ["actor"],
    description: "Teen actor (13-17) with more autonomy",
  },
  {
    id: "test-actor-adult",
    type: "actor_adult",
    ageGroup: "adult",
    name: "Test Adult Actor",
    email: "test.adult@sandbox.castready.dev",
    platform: "callback-zone",
    subscriptionTier: "premium",
    roles: ["actor"],
    description: "Adult actor (18+) with full access",
  },
  {
    id: "test-parent",
    type: "parent",
    ageGroup: "adult",
    name: "Test Parent",
    email: "test.parent@sandbox.castready.dev",
    platform: "callback-zone",
    subscriptionTier: "premium",
    roles: ["parent"],
    description: "Parent managing child accounts",
  },
  // CastReady Coach
  {
    id: "test-coach",
    type: "coach",
    ageGroup: "adult",
    name: "Test Acting Coach",
    email: "test.coach@sandbox.castready.dev",
    platform: "coach",
    subscriptionTier: "premium",
    roles: ["coach"],
    description: "Acting coach with students",
  },
  {
    id: "test-school",
    type: "school",
    ageGroup: "adult",
    name: "Test Studio Admin",
    email: "test.school@sandbox.castready.dev",
    platform: "coach",
    subscriptionTier: "enterprise",
    roles: ["coach", "school"],
    description: "Studio/school administrator",
  },
  // CastReady Castings
  {
    id: "test-casting-actor",
    type: "actor_adult",
    ageGroup: "adult",
    name: "Test Submitting Actor",
    email: "test.castings.actor@sandbox.castready.dev",
    platform: "castings",
    subscriptionTier: "premium",
    roles: ["actor"],
    description: "Actor browsing and submitting to castings",
  },
  {
    id: "test-casting-cd",
    type: "casting_professional",
    ageGroup: "adult",
    name: "Test Casting Director",
    email: "test.cd@sandbox.castready.dev",
    platform: "castings",
    subscriptionTier: "enterprise",
    roles: ["casting_professional"],
    description: "Casting director posting breakdowns",
  },
  {
    id: "test-casting-associate",
    type: "casting_associate",
    ageGroup: "adult",
    name: "Test Casting Associate",
    email: "test.associate@sandbox.castready.dev",
    platform: "castings",
    subscriptionTier: "premium",
    roles: ["casting_associate"],
    description: "Casting associate assisting with organization",
  },
  {
    id: "test-producer",
    type: "producer",
    ageGroup: "adult",
    name: "Test Producer",
    email: "test.producer@sandbox.castready.dev",
    platform: "castings",
    subscriptionTier: "enterprise",
    roles: ["producer"],
    description: "Producer approving final selections",
  },
  // CastReady Pro
  {
    id: "test-pro-agent",
    type: "agent",
    ageGroup: "adult",
    name: "Test Partner Agent",
    email: "test.agent@sandbox.castready.dev",
    platform: "pro",
    subscriptionTier: "premium",
    roles: ["agent"],
    description: "Agent partner with talent pipeline",
  },
  {
    id: "test-pro-manager",
    type: "manager",
    ageGroup: "adult",
    name: "Test Partner Manager",
    email: "test.manager@sandbox.castready.dev",
    platform: "pro",
    subscriptionTier: "premium",
    roles: ["manager"],
    description: "Manager partner with career tools",
  },
  {
    id: "test-vendor",
    type: "vendor",
    ageGroup: "adult",
    name: "Test Industry Professional",
    email: "test.vendor@sandbox.castready.dev",
    platform: "pro",
    subscriptionTier: "basic",
    roles: ["vendor"],
    description: "Industry professional partner",
  },
  {
    id: "test-studio",
    type: "studio",
    ageGroup: "adult",
    name: "Test Studio Executive",
    email: "test.studio@sandbox.castready.dev",
    platform: "pro",
    subscriptionTier: "enterprise",
    roles: ["studio"],
    description: "Studio executive with enterprise access",
  },
];

export const TestModeProvider = ({ children }: { children: ReactNode }) => {
  const { user, hasRole } = useAuth();
  const [isTestMode, setIsTestMode] = useState(false);
  const [testPersona, setTestPersona] = useState<TestPersona | null>(null);
  const [testSessionId, setTestSessionId] = useState<string | null>(null);
  const [simulatedSubscription, setSimulatedSubscriptionState] = useState<SubscriptionTier>("premium");
  const [actionsLog, setActionsLog] = useState<TestAction[]>([]);

  // Only admins can access test mode
  const canAccessTestMode = user && hasRole("admin");

  const generateSessionId = () => {
    return `test-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  };

  const enterTestMode = useCallback(async (persona: TestPersona) => {
    if (!canAccessTestMode || !user) {
      console.warn("Test mode access denied - admin role required");
      return;
    }

    const sessionId = generateSessionId();
    setIsTestMode(true);
    setTestPersona(persona);
    setTestSessionId(sessionId);
    setSimulatedSubscriptionState(persona.subscriptionTier);
    setActionsLog([]);

    // Log session start to database
    try {
      await supabase.from("test_sessions").insert({
        id: sessionId,
        admin_id: user.id,
        persona_id: persona.id,
        persona_type: persona.type,
        platform: persona.platform,
        subscription_simulated: persona.subscriptionTier,
        actions_performed: [],
      });
    } catch (error) {
      console.error("Failed to log test session start:", error);
    }

    testModeLogger.debug(`Started session ${sessionId} as ${persona.name}`);
  }, [canAccessTestMode, user]);

  const exitTestMode = useCallback(async () => {
    if (testSessionId && user) {
      // Log session end to database
      try {
        await supabase
          .from("test_sessions")
          .update({
            ended_at: new Date().toISOString(),
            actions_performed: actionsLog as unknown as Json,
          })
          .eq("id", testSessionId);
      } catch (error) {
        console.error("Failed to log test session end:", error);
      }
    }

    testModeLogger.debug(`Ended session ${testSessionId}`);
    setIsTestMode(false);
    setTestPersona(null);
    setTestSessionId(null);
    setActionsLog([]);
  }, [testSessionId, user, actionsLog]);

  const setSimulatedSubscription = useCallback((tier: SubscriptionTier) => {
    setSimulatedSubscriptionState(tier);
    logTestAction({
      action: "simulated_action",
      target: "subscription",
      details: { newTier: tier },
    });
  }, []);

  const logTestAction = useCallback((action: Omit<TestAction, "timestamp">) => {
    const fullAction: TestAction = {
      ...action,
      timestamp: new Date().toISOString(),
    };
    setActionsLog(prev => [...prev, fullAction]);
  }, []);

  // Writes are blocked in test mode
  const isWriteBlocked = isTestMode;

  return (
    <TestModeContext.Provider
      value={{
        isTestMode,
        testPersona,
        testSessionId,
        simulatedSubscription,
        actionsLog,
        enterTestMode,
        exitTestMode,
        setSimulatedSubscription,
        logTestAction,
        isWriteBlocked,
        canAccessTestMode: !!canAccessTestMode,
      }}
    >
      {children}
    </TestModeContext.Provider>
  );
};

export const useTestMode = () => {
  const context = useContext(TestModeContext);
  if (context === undefined) {
    throw new Error("useTestMode must be used within a TestModeProvider");
  }
  return context;
};
